/* Zana for the v3 console: the same ask the classic dock uses (briefingsApi
   askZana), shaped into the design's answer card — a one-line answer, the facts
   under it, and the records it came from. */
import { askZana as askZanaApi, AskTurn } from '../../services/briefingsApi';
import type { FleetModel } from './model';
import type { PageKey } from '../kit';
import { mergeScreen, ScreenLook } from '../zana/screen';

export interface ZanaSource { label: string; page: PageKey; search?: string }
export interface ZanaAnswer {
  headline: string;
  facts: string[];
  note: string | null;
  sources: ZanaSource[];
  at: Date;
  raw: string;
}

/** What a page tells Zana about what is on it. Plates are normalised (no
 *  spaces, upper case) because the backend matches buses by plate. */
export interface ZanaScreen {
  entityId?: string;                            // the bus selected on this page
  window?: string;                              // "last 30 days", "this term"
  cards?: { label: string; value: string }[];   // the figures on this page
  focus?: string[];                             // plates visibly picked out on this page
}
interface MetaLike { key: string; title: string; zanaContext: string; zana?: ZanaScreen }
export const platePlain = (plate: string) => String(plate || '').toUpperCase().replace(/[\s-]/g, '');

const PAGE_NAME: Record<string, string> = {
  ConsoleOverview: 'overview', ConsoleLiveMap: 'live-map', ConsoleAlerts: 'alerts', ConsoleBuses: 'buses',
  ConsoleFuel: 'fuel', ConsoleMaintenance: 'maintenance', ConsoleTrips: 'trips', ConsolePeople: 'people',
  ConsoleReport: 'reports', ConsoleAnalytics: 'analytics', ConsoleFuelPrices: 'fuel-prices', ConsoleSettings: 'settings', ConsoleBilling: 'billing', ConsoleZanaChat: 'zana',
};

/** Split a free-text answer into the card's headline, bullet facts and closing note. */
export function shapeAnswer(text: string, f: FleetModel, meta: MetaLike): ZanaAnswer {
  const clean = text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/^#+\s*/gm, '').trim();
  const lines = clean.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const facts: string[] = [];
  const prose: string[] = [];
  lines.forEach((l) => {
    const m = l.match(/^(?:[-•*]|\d+[.)])\s+(.*)$/);
    if (m) facts.push(m[1]); else prose.push(l);
  });
  let headline = prose.shift() || facts.shift() || clean;
  /* The first line is often a warm-up. The answer is that line plus the next
     one, kept together in the bold. The rest of the prose is the working. */
  if (prose.length && headline.length < 320 && headline.length + prose[0].length <= 520) {
    headline = `${headline} ${prose.shift()}`;
  }
  let note: string | null = prose.join(' ') || null;
  if (headline.length > 520) {
    const cut = headline.lastIndexOf('. ', 480);
    if (cut > 80) { note = [headline.slice(cut + 2), note].filter(Boolean).join(' '); headline = headline.slice(0, cut + 1); }
  }
  const sources: ZanaSource[] = [];
  const seen = new Set<string>();
  f.buses.forEach((b) => {
    if (sources.length >= 3 || !b.plate) return;
    if (clean.toUpperCase().includes(b.plate.toUpperCase()) && !seen.has(b.id)) {
      seen.add(b.id);
      sources.push({ label: b.plate, page: 'ConsoleBuses', search: `?bus=${encodeURIComponent(b.id)}` });
    }
  });
  if (/fuel|litre|refill|km per/i.test(clean)) sources.push({ label: 'Fuel records', page: 'ConsoleFuel' });
  else if (/service|insurance|inspection|ntsa/i.test(clean)) sources.push({ label: 'Service log', page: 'ConsoleMaintenance' });
  else if (/alert/i.test(clean)) sources.push({ label: 'Alerts', page: 'ConsoleAlerts' });
  if (!sources.length && meta.key in PAGE_NAME && meta.key !== 'ConsoleZanaChat') sources.push({ label: meta.title, page: meta.key as PageKey });
  return { headline, facts, note, sources, at: new Date(), raw: text };
}

export async function askZana(question: string, meta: MetaLike, f: FleetModel, history: AskTurn[] = [], look?: ScreenLook): Promise<ZanaAnswer> {
  if (!f.org.code) throw new Error('no org');
  const unread = f.alerts.filter((a) => !a.read);
  /* The page's own screen, when it said what is on it, plus one look at the
     page body taken as the question is asked. Focus used to be "buses that
     need a look" on EVERY page, so Zana claimed to be pointing at buses the
     page did not show; it now defaults to that only on Overview, whose To-do
     list is exactly those buses (2026-10-26). A look replaces that default
     with the plates actually on the page. */
  const seen = look || { cards: [], plates: [] };
  const merged = mergeScreen(meta.zana, seen);
  const focus = merged.focus && merged.focus.length
    ? merged.focus
    : (meta.key === 'ConsoleOverview' && !look ? f.buses.filter((b) => b.needsLook).map((b) => platePlain(b.plate)) : []);
  const res = await askZanaApi(
    f.org.code,
    question,
    {
      page: PAGE_NAME[meta.key] || meta.key,
      ...(merged.entityId ? { entityType: 'bus' as const, entityId: merged.entityId } : {}),
      ...(merged.window ? { window: merged.window } : {}),
      ...(seen.selected ? { selected: { bus: seen.selected } } : {}),
      ...(seen.plates.length ? { visible_rows: seen.plates.slice(0, 12) } : {}),
      cards: [
        { label: 'Fleet buses', value: String(f.buses.length) },
        { label: 'Active buses', value: String(f.buses.filter((b) => b.status === 'on_route' || b.status === 'late').length) },
        ...(merged.cards || []),
      ].slice(0, 8),
      focus: focus.filter(Boolean).slice(0, 12).map((id) => ({ type: 'bus' as const, id: platePlain(id) })),
    },
    history,
    {
      unreadAlertCount: unread.length,
      highRiskBusCount: new Set(unread.filter((a) => a.severity === 'urgent').map((a) => a.busId).filter(Boolean)).size,
      unreadBusIds: unread.map((a) => a.busId).filter((x): x is string => !!x),
    },
  );
  if (!res.ok || !res.answer) throw new Error(res.message || 'Zana could not answer');
  return shapeAnswer(res.answer, f, meta);
}
